import React, { useState } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";

const CreateFacilityWalletModal = ({
  show,
  onClose,
  onSuccess,
  facilityId,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    walletType: "",
    amount: 0,
    description: "",
  });
  const [errors, setErrors] = useState({});

  // Available wallet types for facility wallets
  const walletTypes = [
    {
      value: "levy",
      label: "Levy Management",
      description: "For managing levy payments and collections",
    },
    {
      value: "propertyManagement",
      label: "Property Management",
      description: "For property management fees and expenses",
    },
    {
      value: "lease",
      label: "Lease Management",
      description: "For lease payments and rental transactions",
    },
    {
      value: "utility",
      label: "Utility Management",
      description: "For utility bills and payments",
    },
    {
      value: "vas",
      label: "Value Added Services",
      description: "For additional services and amenities",
    },
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) || 0 : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.walletType) {
      newErrors.walletType = "Please select a wallet type";
    }

    if (formData.amount < 0) {
      newErrors.amount = "Initial amount cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const walletData = {
        owner: facilityId, // The facility itself is the owner
        ownerType: "Facility",
        walletType: formData.walletType,
        amount: formData.amount,
        isActive: true,
      };

      console.log("📡 Creating facility wallet:", walletData);

      const response = await makeRequest2(
        `/api/app/wallet/${facilityId}/create`,
        "POST",
        walletData,
      );

      console.log("📥 Create wallet response:", response);

      if (
        response.success ||
        response.message === "Wallet created successfully"
      ) {
        toastify(
          `${walletTypes.find((type) => type.value === formData.walletType)?.label} wallet created successfully`,
          "success",
        );

        // Reset form
        setFormData({
          walletType: "",
          amount: 0,
          description: "",
        });

        // Call success callback
        onSuccess();

        // Close modal
        onClose();
      } else {
        toastify(response.error || "Failed to create wallet", "error");
      }
    } catch (error) {
      console.error("❌ Error creating wallet:", error);
      toastify(error.message || "Error creating wallet", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      walletType: "",
      amount: 0,
      description: "",
    });
    setErrors({});
    onClose();
  };

  if (!show) return null;

  const selectedWalletType = walletTypes.find(
    (type) => type.value === formData.walletType,
  );

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center py-3 px-4">
            <h5 className="modal-title">
              <i className="fa fa-plus-circle mr-2"></i>
              Create Facility Wallet
            </h5>
            <button
              type="button"
              className="btn btn-light btn-sm rounded-circle p-2 ml-3"
              onClick={onClose}
              style={{
                width: "40px",
                height: "40px",
                border: "1px solid #dee2e6",
                fontSize: "18px",
                lineHeight: "1",
              }}
            >
              <span className="text-muted">&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Info Alert */}
              <div className="alert alert-info">
                <i className="fa fa-info-circle mr-2"></i>
                <strong>Facility Wallets:</strong> Create specialized wallets
                for different modules of your facility management system. Each
                wallet type handles specific transaction categories.
              </div>

              {/* Wallet Type Selection */}
              <div className="form-group">
                <label>
                  Wallet Type <span className="text-danger">*</span>
                </label>
                <select
                  name="walletType"
                  className={`form-control ${errors.walletType ? "is-invalid" : ""}`}
                  value={formData.walletType}
                  onChange={handleInputChange}
                >
                  <option value="">Select wallet type...</option>
                  {walletTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors.walletType && (
                  <div className="invalid-feedback">{errors.walletType}</div>
                )}
              </div>

              {/* Wallet Type Description */}
              {selectedWalletType && (
                <div className="card border-primary mb-3">
                  <div className="card-body">
                    <h6 className="card-title text-primary">
                      {selectedWalletType.label}
                    </h6>
                    <p className="card-text text-muted mb-0">
                      {selectedWalletType.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Initial Amount */}
              <div className="form-group">
                <label>Initial Amount</label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">KES</span>
                  </div>
                  <input
                    type="number"
                    name="amount"
                    className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">{errors.amount}</div>
                  )}
                </div>
                <small className="text-muted">
                  You can leave this as 0 and add funds later through top-ups
                </small>
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows="3"
                  placeholder="Additional notes about this wallet..."
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength="200"
                ></textarea>
                <small className="text-muted">
                  {formData.description.length}/200 characters
                </small>
              </div>

              {/* Wallet Preview */}
              {formData.walletType && (
                <div className="card border-success">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">Wallet Preview</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Type:</strong>
                          <span className="badge badge-primary ml-2">
                            {selectedWalletType?.label}
                          </span>
                        </p>
                        <p className="mb-1">
                          <strong>Owner:</strong> Facility
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Initial Balance:</strong>
                          <span className="text-success ml-2">
                            KES {formData.amount.toLocaleString()}
                          </span>
                        </p>
                        <p className="mb-1">
                          <strong>Status:</strong>
                          <span className="badge badge-success ml-2">
                            Active
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading || !formData.walletType}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fa fa-plus mr-1"></i>
                    Create Facility Wallet
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateFacilityWalletModal;
